import { ShieldCheckIcon, WalletIcon } from '@phosphor-icons/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { close } from 'ionicons/icons';
import moment from 'moment';

import { usePets } from '../../context/PetsContext';
import { useGetDatosPago, useGetMetodosPago, usePostDatosPago } from '../../hooks/Pago/usePago';
import { useChangeCoverageToBbva } from '../../hooks/Pet/usePet';
import { useGetPlanes, useGetPreciosPlanesByEdadMascota } from '../../hooks/Plan/usePlanes';
import { useAlert } from '../../hooks/UI/useAlert';
import { useStepNavigation } from '../../hooks/UI/useStepNavigation';
import { MedioPagoReq } from '../../types/Pago/types';
import { Pet, PetForm_3 as PetFormType_3 } from '../../types/Pet/types';
import { getApiErrorMessage } from '../../utils/apiHealth';
import {
  isDuplicatedMedioPago,
  normalizeNumeroPago,
  normalizeVencimiento,
} from '../../utils/pago';
import Alert from '../UI/Alert';
import FormHeader from '../UI/FormHeader';
import Navigation from '../UI/Navigation';
import Spinner from '../UI/Spinner';
import StepWrapper from '../UI/StepWrapper';
import PetFormStep2 from './PetForm_2';
import PetFormStep3 from './PetForm_3';
import './PlanPaymentForm.scss';

const steps = [
  {
    icon: <ShieldCheckIcon weight="fill" />,
    title: 'Elegir Plan',
    subtitle: 'Datos de pago',
  },
  {
    icon: <WalletIcon weight="fill" />,
    title: 'Datos de pago',
    subtitle: 'Completá el método de cobro',
  },
];

type PlanPaymentFormProps = {
  pet: Pet;
  closeModal?: () => void;
};

const PlanPaymentForm: React.FC<PlanPaymentFormProps> = ({ pet, closeModal }) => {
  const { mutate: mutateMascotas, setCurrentPet } = usePets();
  const { currentStep, totalSteps, isFirst, next, back } = useStepNavigation(
    steps.length,
  );
  const { alertState, showAlert, hideAlert } = useAlert();

  const { planes, isValidating: isValidatingPlanes } = useGetPlanes(true);
  const { datosPago, mutate: mutateDatosPago } = useGetDatosPago(true);
  const { metodosPago } = useGetMetodosPago(true);
  const { postDatosPago, isLoading: isPostingPayment } = usePostDatosPago();
  const { changeCoverageToBbva, isLoading: isChangingCoverage } =
    useChangeCoverageToBbva();

  const [plan, setPlan] = useState<number>();
  const [, setIsCodVendedorAlertOpen] = useState(false);
  const codVendedorAlertShownRef = useRef(true);

  const edadMascota = useMemo(() => {
    if (!pet?.fecha_nacimiento) return undefined;
    return moment().diff(moment(pet.fecha_nacimiento), 'years');
  }, [pet]);

  const { preciosPlanes, isValidating: isValidatingPrecios } =
    useGetPreciosPlanesByEdadMascota(edadMascota, true);

  const showError = useCallback(
    (message: string) => {
      showAlert({
        header: 'No pudimos continuar',
        message,
        buttons: [{ text: 'Entendido' }],
      });
    },
    [showAlert],
  );

  const onSubmit = useCallback(
    async (medioPagoValues: PetFormType_3): Promise<boolean> => {
      if (!plan) {
        showError('Elegí un plan para poder continuar.');
        return false;
      }

      try {
        const tipo = Number(medioPagoValues.tipo);
        const esCBU = !!medioPagoValues.cbu;
        const noRequiereVencimiento = esCBU || tipo === 30 || tipo === 31;

        const medioPagoFormatted: MedioPagoReq = {
          tipo,
          numero_pago: normalizeNumeroPago(
            medioPagoValues.numeroTarjeta ?? medioPagoValues.cbu,
          ),
          vencimiento: noRequiereVencimiento
            ? null
            : normalizeVencimiento(medioPagoValues.vencimiento),
        };

        const pagoYaExiste = datosPago?.find((pago) =>
          isDuplicatedMedioPago(pago, medioPagoFormatted),
        );

        const pagoCreado = pagoYaExiste
          ? undefined
          : await postDatosPago(medioPagoFormatted);

        if (!pagoYaExiste && !pagoCreado?.id) {
          showError('No pudimos guardar los datos de pago.');
          return false;
        }

        const datosActualizados = await mutateDatosPago();
        const pagoSeleccionado =
          pagoYaExiste ??
          datosActualizados?.find((pago) =>
            isDuplicatedMedioPago(pago, medioPagoFormatted),
          ) ??
          pagoCreado;

        if (!pagoSeleccionado) {
          showError('No pudimos confirmar el método de pago.');
          return false;
        }

        const success = await changeCoverageToBbva({
          mascotaId: pet.id,
          planId: plan,
          datosPagoId: pagoSeleccionado.id,
        });

        if (!success) {
          showError('No pudimos actualizar la cobertura. Intentá nuevamente.');
          return false;
        }

        showAlert({
          header: 'Listo',
          message: 'Guardamos los datos de pago y actualizamos el plan.',
          buttons: [{ text: 'Entendido' }],
          onDidDismiss: async () => {
            setCurrentPet((current) =>
              current?.id === pet.id
                ? { ...current, compania: 'BBVA', fechaContratacion: null }
                : current,
            );
            await mutateMascotas();
            closeModal?.();
          },
        });

        return true;
      } catch (err) {
        showError(
          getApiErrorMessage(
            err,
            'Ocurrió un error al guardar los datos. Intentá nuevamente.',
          ),
        );
        return false;
      }
    },
    [
      plan,
      datosPago,
      pet.id,
      postDatosPago,
      mutateDatosPago,
      changeCoverageToBbva,
      showAlert,
      showError,
      mutateMascotas,
      setCurrentPet,
      closeModal,
    ],
  );

  const isSubmitting = isPostingPayment || isChangingCoverage;
  const isLoading = currentStep === 1 && (isValidatingPlanes || isValidatingPrecios);

  if (isLoading) {
    return <Spinner fullscreen />;
  }

  return (
    <div className="dawer-plan-payment-form">
      <Navigation
        startIcon={isFirst ? close : undefined}
        onBack={isFirst ? closeModal : back}
        currentStep={currentStep}
        totalSteps={totalSteps}
      />

      <div className="ion-padding-horizontal">
        <FormHeader {...steps[currentStep - 1]} />

        <StepWrapper show={currentStep === 1}>
          <PetFormStep2
            planes={planes}
            plan={plan}
            setPlan={setPlan}
            preciosPlanes={preciosPlanes ?? []}
            goForward={next}
          />
        </StepWrapper>

        <StepWrapper show={currentStep === 2}>
          <PetFormStep3
            metodosPago={metodosPago}
            setIsCodVendedorAlertOpen={setIsCodVendedorAlertOpen}
            codVendedorAlertShownRef={codVendedorAlertShownRef}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        </StepWrapper>
      </div>

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />
    </div>
  );
};

export default React.memo(PlanPaymentForm);
